import React, { useMemo, useState } from 'react';

import type { ColumnDef } from '@tanstack/react-table';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { toast } from 'react-toastify';
import { Trash2 } from 'lucide-react';
import { round } from 'lodash';

import { PAGES } from '../../constants/pages';
import { AiOutlineInfoCircle } from 'react-icons/ai';

import {
    GetTransferableProductsQuery,
    MicrostoreElementState,
    StockVariationType,
    useCreateMultiStockVariationMutation,
    useGetTransferableProductsQuery,
} from 'data/__generated__';

import { Footer } from 'components/Footer';
import { Header, HeaderTitle } from 'components/Header';
import { TotemPrimaryButton } from 'components/TotemPrimaryButton';
import { TotemSelect } from 'components/TotemSelect';
import { TotemInput } from 'components/TotemInput';
import { TotemCsvInputButton } from 'components/TotemCsvInputButton';
import { CsvImportInfoButton } from 'components/CsvImportInfoButton';
import { PageTitle } from 'components/PageTitle';
import { Card, CardContent } from 'components/ui/card';
import { Button } from 'components/ui/button';
import { Input } from 'components/ui/input';
import { DataTable, textColumn } from 'components/DataTable';
import { ColumnSelect } from 'components/Select/ColumnSelect';
import { ProductWithStocksSelector } from './components/ProductWithStocksSelector';
import { handleMultiStockVariationCsvImport, ProductDeltaRow } from './components/multiStockVariationCsvImport';
import { stockVariationTypeIsNegativeVariation, siteVariationsLabels } from './constants';

type TransferableProduct = GetTransferableProductsQuery['transferableProducts'][number];

type FormType = {
    columnId: string | null;
    productRows: ProductDeltaRow[];
    explanation: string | null;
    type: StockVariationType | null;
};

const initialFormData: FormType = {
    columnId: null,
    productRows: [],
    explanation: null,
    type: null,
};

const toProductRow = (product: TransferableProduct, delta = 0): ProductDeltaRow => ({
    productId: product.productId,
    label: product.fullname,
    delta,
    conditionning: product.conditionning,
    packagesPerBatch: product.packagesPerBatch,
    originStockAvailable: product.originStockAvailable,
});

const SELECTABLE_COLUMN_STATES = [MicrostoreElementState.Regular];

export const StockVariationMultiCreate = () => {
    const [formData, setFormData] = useState<FormType>(initialFormData);
    const [createStockVariation] = useCreateMultiStockVariationMutation();

    const { data: transferableProductsData } = useGetTransferableProductsQuery({
        variables: {
            originColumnId: formData.columnId ?? '',
            destinationColumnId: formData.columnId ?? '',
        },
        skip: !formData.columnId,
        fetchPolicy: 'no-cache',
    });
    const transferableProducts = transferableProductsData?.transferableProducts ?? [];

    const deltaSign: 1 | -1 = formData.type && stockVariationTypeIsNegativeVariation[formData.type] ? -1 : 1;

    const setProductRows = (updater: ProductDeltaRow[] | ((prev: ProductDeltaRow[]) => ProductDeltaRow[])) => {
        setFormData((prev) => ({
            ...prev,
            productRows: typeof updater === 'function' ? updater(prev.productRows) : updater,
        }));
    };

    const handleCsvImport = (csvData: string[][]) => {
        handleMultiStockVariationCsvImport({
            csvData,
            transferableProducts,
            deltaSign,
            setProductRows,
        });
    };

    const typeOptions = Object.values(StockVariationType).map((s) => ({
        label: `${siteVariationsLabels[s]} (${s})`,
        value: s,
    }));

    const handleColumnSelect = (columnId: string | null) => {
        setFormData({ ...initialFormData, columnId });
    };

    const handleProductSelect = (productId: string) => {
        if (formData.productRows.some((p) => p.productId === productId)) return;
        const product = transferableProducts.find((p) => p.productId === productId);
        if (!product) return;
        setProductRows([...formData.productRows, toProductRow(product)]);
    };

    const handleTypeSelect = (typeValue: StockVariationType | null) => {
        if (!typeValue) return;
        const isNegative = stockVariationTypeIsNegativeVariation[typeValue];
        const productRows = formData.productRows.map((row) => {
            const shouldFlipSign = (isNegative && row.delta > 0) || (!isNegative && row.delta < 0);
            return shouldFlipSign ? { ...row, delta: -row.delta } : row;
        });
        setFormData({ ...formData, type: typeValue, productRows });
    };

    const updateRowDelta = (productId: string, delta: number) => {
        setProductRows((rows) => rows.map((row) => (row.productId === productId ? { ...row, delta } : row)));
    };

    const handleSignedQuantityInput = (value: string, row: ProductDeltaRow) => {
        if (!/^-?\d*$/.test(value)) return;
        const numberValue = value === '' || value === '-' ? 0 : Number(value);
        // Reject inputs with the wrong sign for the selected variation type.
        const isNegative = deltaSign === -1;
        if ((isNegative && numberValue > 0) || (!isNegative && numberValue < 0)) return;
        updateRowDelta(row.productId, numberValue);
    };

    const handlePalletsInput = (value: string, row: ProductDeltaRow) => {
        if (!/^\d*\.?\d{0,2}$/.test(value)) return;
        const abs = value === '' ? 0 : Math.round(Number(value) * row.conditionning * row.packagesPerBatch);
        updateRowDelta(row.productId, abs * deltaSign);
    };

    const handleColisInput = (value: string, row: ProductDeltaRow) => {
        if (!/^\d*\.?\d{0,2}$/.test(value)) return;
        const abs = value === '' ? 0 : Math.round(Number(value) * row.conditionning);
        updateRowDelta(row.productId, abs * deltaSign);
    };

    const handleProductDelete = (productId: string) => {
        setProductRows((rows) => rows.filter((p) => p.productId !== productId));
    };

    const handleExplanationInput = (value: string) => {
        setFormData({ ...formData, explanation: value });
    };

    const productColumns = useMemo<ColumnDef<ProductDeltaRow, unknown>[]>(
        () => [
            textColumn<ProductDeltaRow, Record<string, unknown>>({
                accessorKey: 'label',
                header: 'Produit',
                defaultVisible: true,
                cell: ({ row }) => <span className="font-medium">{row.original.label}</span>,
            }),
            textColumn<ProductDeltaRow, Record<string, unknown>>({
                accessorKey: 'originStockAvailable',
                header: 'Stock dispo',
                defaultVisible: true,
                cell: ({ row }) => <Centered>{row.original.originStockAvailable}</Centered>,
            }),
            textColumn<ProductDeltaRow, Record<string, unknown>>({
                accessorKey: 'conditionning',
                header: 'Colisage',
                defaultVisible: true,
                cell: ({ row }) => <Centered>{row.original.conditionning}</Centered>,
            }),
            textColumn<ProductDeltaRow, Record<string, unknown>>({
                accessorKey: 'packagesPerBatch',
                header: 'Palettisation',
                defaultVisible: true,
                cell: ({ row }) => <Centered>{row.original.packagesPerBatch}</Centered>,
            }),
            textColumn<ProductDeltaRow, Record<string, unknown>>({
                id: 'pallets',
                accessorFn: () => '',
                header: 'Palettes',
                defaultVisible: true,
                cell: ({ row }) => {
                    const r = row.original;
                    if (r.packagesPerBatch <= 1) return null;
                    const value = round(Math.abs(r.delta) / (r.conditionning * r.packagesPerBatch), 2) || 0;
                    return (
                        <CompactInput
                            type="number"
                            value={value}
                            min={0}
                            step="1"
                            onChange={(e) => handlePalletsInput(e.target.value, r)}
                        />
                    );
                },
            }),
            textColumn<ProductDeltaRow, Record<string, unknown>>({
                id: 'colis',
                accessorFn: () => '',
                header: 'Colis',
                defaultVisible: true,
                cell: ({ row }) => {
                    const r = row.original;
                    if (r.conditionning <= 1) return null;
                    const value = round(Math.abs(r.delta) / r.conditionning, 2) || 0;
                    return (
                        <CompactInput
                            type="number"
                            value={value}
                            min={0}
                            step="1"
                            onChange={(e) => handleColisInput(e.target.value, r)}
                        />
                    );
                },
            }),
            textColumn<ProductDeltaRow, Record<string, unknown>>({
                id: 'quantity',
                accessorFn: (row) => row.delta,
                header: 'Quantité',
                defaultVisible: true,
                cell: ({ row }) => (
                    <CompactInput
                        type="number"
                        value={row.original.delta ?? 0}
                        onChange={(e) => handleSignedQuantityInput(e.target.value, row.original)}
                        data-test="product-delta"
                    />
                ),
            }),
            textColumn<ProductDeltaRow, Record<string, unknown>>({
                id: 'actions',
                accessorFn: () => '',
                header: '',
                defaultVisible: true,
                cell: ({ row }) => (
                    <Button
                        type="button"
                        variant="ghost"
                        size="icon-sm"
                        onClick={() => handleProductDelete(row.original.productId)}
                        aria-label="Supprimer"
                    >
                        <Trash2 className="size-4 text-destructive" />
                    </Button>
                ),
            }),
        ],
        [deltaSign],
    );

    const handleSubmit = async () => {
        const { columnId, productRows, explanation, type } = formData;
        const notNullRows = productRows.filter(({ delta }) => delta !== 0);
        if (!explanation || !columnId || !notNullRows.length || !type) {
            toast.error("L'un des champs n'a pas été renseigné");
            return;
        }
        const typeLabel = typeOptions.find((o) => o.value === type)?.label ?? type;
        const result = await createStockVariation({
            variables: {
                columnId,
                productIdsAndDeltas: notNullRows.map(({ delta, productId }) => ({ delta, productId })),
                explanation,
                type,
            },
        });

        const errors = (result.data?.createMultiStockVariation ?? [])
            .map(({ error }) => error)
            .filter((error): error is string => !!error);
        if (errors.length) {
            throw new Error(`L'erreur suivante est survenue : ${errors.join(', ')}`);
        }

        setFormData(initialFormData);
        toast.success(`Variations ${typeLabel} appliquées (${notNullRows.length} produits).`);
    };

    return (
        <Container>
            <Header>
                <HeaderTitle>
                    <PageTitle page={PAGES.stockVariationCreate} />
                </HeaderTitle>
            </Header>
            <Content>
                <Form>
                    <ContentScrollable>
                        <Card className="bg-white">
                            <CardContent>
                                <Fields>
                                    <Info>
                                        <AiOutlineInfoCircle /> Une variation de stock est un changement particulier de
                                        stock existant (ex: si le stock est de 50 et que l'on achète un coca (variation
                                        de stock = 1), le nouveau stock est 49). Pour modifier le stock absolu (ex: 45
                                        au lieu de 50):{' '}
                                        <Link to="/site-inventory-stock/create">/site-inventory-stock/create</Link>
                                    </Info>

                                    <ColumnSelect
                                        label="Colonne"
                                        value={formData.columnId}
                                        onChange={handleColumnSelect}
                                        isClearable
                                        dataTest="column-selector"
                                        states={SELECTABLE_COLUMN_STATES}
                                    />

                                    <TotemSelect
                                        label="Choisir le type de variation de stock"
                                        placeholder="Sélectionner un type"
                                        value={formData.type}
                                        options={typeOptions}
                                        onChange={handleTypeSelect}
                                        dataTest="type-selector"
                                    />

                                    {formData.type ? (
                                        <TotemInput
                                            label="Pourquoi souhaitez-vous modifier le stock ?"
                                            placeholder="Renseigner la raison de la modification de stock"
                                            type="string"
                                            value={formData.explanation ?? ''}
                                            onChange={handleExplanationInput}
                                            data-test="stock-variation-explanation"
                                        />
                                    ) : null}

                                    {formData.columnId ? (
                                        <ProductPickerRow>
                                            <ProductPickerSelect>
                                                <ProductWithStocksSelector
                                                    productOption={null}
                                                    handleProductSelect={handleProductSelect}
                                                    columnId={formData.columnId}
                                                />
                                            </ProductPickerSelect>
                                            <TotemCsvInputButton onCsvDataUpload={handleCsvImport} label="Import CSV" />
                                            <CsvImportInfoButton title="Format du fichier CSV">
                                                <p>
                                                    Le fichier doit contenir une ligne d'en-tête avec la colonne{' '}
                                                    <Code>productId</Code> et au moins une des colonnes de quantité
                                                    ci-dessous (par ordre de priorité) :
                                                </p>
                                                <ul className="ml-5 list-disc space-y-1">
                                                    <li>
                                                        <Code>quantity</Code> — nombre d'unités (entier).
                                                    </li>
                                                    <li>
                                                        <Code>pallets</Code> — nombre de palettes ; converti en unités
                                                        via <em>colisage × palettisation</em>.
                                                    </li>
                                                    <li>
                                                        <Code>colis</Code> — nombre de colis ; converti en unités via{' '}
                                                        <em>colisage</em>.
                                                    </li>
                                                </ul>
                                                <p>
                                                    Si plusieurs colonnes sont remplies, l'ordre de priorité est{' '}
                                                    <Code>quantity</Code> &gt; <Code>pallets</Code> &gt;{' '}
                                                    <Code>colis</Code>. Le signe est ajusté automatiquement selon le
                                                    type de variation sélectionné (négatif pour une perte, positif pour
                                                    un ajout).
                                                </p>
                                                <p>
                                                    Les produits déjà présents dans la liste voient leur quantité mise à
                                                    jour ; les nouveaux sont ajoutés. Les doublons et les{' '}
                                                    <Code>productId</Code> introuvables sont signalés.
                                                </p>
                                                <p className="text-muted-foreground">
                                                    Exemple : <Code>productId,quantity</Code>
                                                    <br />
                                                    <Code>abc-123,5</Code>
                                                </p>
                                            </CsvImportInfoButton>
                                        </ProductPickerRow>
                                    ) : null}

                                    {formData.productRows.length ? (
                                        <DataTable<ProductDeltaRow>
                                            columns={productColumns}
                                            data={formData.productRows}
                                            filterMode="client"
                                            getRowId={(row) => row.productId}
                                        />
                                    ) : null}
                                </Fields>
                            </CardContent>
                        </Card>
                    </ContentScrollable>
                    <Footer>
                        <TotemPrimaryButton
                            type="button"
                            data-test="stock-variation-create_submit-button"
                            onClick={handleSubmit}
                        >
                            Ajouter les variations de stock
                        </TotemPrimaryButton>
                    </Footer>
                </Form>
            </Content>
        </Container>
    );
};

const Container = styled.div`
    display: flex;
    flex-direction: column;
    flex: 1;
    height: 100%;
    background-color: ${({ theme }) => theme.backgroundColor};
`;

const Content = styled.div`
    flex: 1;
    overflow: hidden;
`;

const Form = styled.form`
    height: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
`;

const ContentScrollable = styled.div`
    padding: 15px;
    width: 100%;
    flex: 1;
    overflow-y: auto;
`;

const Fields = styled.div`
    display: flex;
    flex-direction: column;
    width: 100%;
    gap: 15px;
`;

const Info = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    color: ${({ theme }) => theme.infoTextColor};
    font-size: 14px;
`;

const ProductPickerRow = styled.div`
    display: flex;
    flex-direction: row;
    align-items: flex-end;
    gap: 12px;
    width: 100%;
`;

const ProductPickerSelect = styled.div`
    flex: 1;
`;

const Centered = ({ children }: { children: React.ReactNode }) => <div className="text-center">{children}</div>;

const CompactInput = (props: React.ComponentProps<typeof Input>) => (
    <Input {...props} className="h-8 w-20 text-center" />
);

const Code = ({ children }: { children: React.ReactNode }) => (
    <code className="rounded bg-muted px-1 py-0.5 text-xs">{children}</code>
);
